import { useEffect, useRef, useState } from 'react';

export type PhaseStepState = 'done' | 'active' | 'pending';

interface ISimulatedPhases {
    steps: [PhaseStepState, PhaseStepState, PhaseStepState];
    // 0..1 — eased toward 0.9 while waiting; snaps to 1 on settle
    progress: number;
    activeStepNumber: number;
    isOvertime: boolean;
}

const TICK_MS = 250;
// Response arrived → flip everything to done + 100%, hold briefly so the
// customer sees the story complete, then the parent swaps in the table.
const SETTLE_MS = 700;

/**
 * Timed phase simulation for the "preparing your quote" loader (v1 — FE
 * only). Active-quotes fetches give no real progress signal, so phases are
 * paced against the typical duration per service type. A future backend
 * progress signal plugs in here: replace the timer state with a
 * polled/streamed phase value and keep the returned shape identical.
 */
export const useSimulatedPhases = (typicalMs: number, isSettling: boolean, onSettled: () => void): ISimulatedPhases => {
    const [elapsedMs, setElapsedMs] = useState(0);
    const onSettledRef = useRef(onSettled);
    onSettledRef.current = onSettled;

    useEffect(() => {
        if (isSettling) return undefined;
        const startedAt = Date.now();
        const interval = setInterval(() => {
            setElapsedMs(Date.now() - startedAt);
        }, TICK_MS);

        return () => clearInterval(interval);
    }, [isSettling]);

    useEffect(() => {
        if (!isSettling) return undefined;
        const timeout = setTimeout(() => onSettledRef.current(), SETTLE_MS);

        return () => clearTimeout(timeout);
    }, [isSettling]);

    if (isSettling) {
        return {
            steps: ['done', 'done', 'done'],
            progress: 1,
            activeStepNumber: 3,
            isOvertime: false,
        };
    }

    // Step 1 (details received) is true the moment the loader mounts. Step 3
    // takes over at ~70% of the typical wait so the story keeps moving even
    // though the request is a single opaque call.
    const step3Active = elapsedMs >= typicalMs * 0.7;
    // Asymptotic ease toward 90% — the bar visibly moves early, slows late,
    // and never sits at full while we are still waiting.
    const progress = 0.9 * (1 - Math.exp(-elapsedMs / (typicalMs * 0.55)));

    return {
        steps: step3Active ? ['done', 'done', 'active'] : ['done', 'active', 'pending'],
        progress,
        activeStepNumber: step3Active ? 3 : 2,
        isOvertime: elapsedMs > typicalMs * 2,
    };
};
